import { createColumnHelper } from "@tanstack/react-table";

import CrudActions from "components/crud/CrudActions";

const columnHelper = createColumnHelper();

const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return "border-green-200 bg-green-50 text-green-700";

    case "inactive":
      return "border-red-200 bg-red-50 text-red-700";

    case "prospect":
      return "border-blue-200 bg-blue-50 text-blue-700";

    case "closed":
      return "border-gray-200 bg-gray-50 text-gray-700";

    default:
      return "border-gray-200 bg-gray-50 text-gray-700";
  }
};

export const schoolColumns = ({
  onEdit,
  onDelete,
  onView,
} = {}) => [
  // =========================================================
  // PERMANENT SCHOOL ID
  // =========================================================

  columnHelper.accessor("permanentSchoolId", {
    id: "permanentSchoolId",

    header: "Permanent School ID",

    cell: (info) => (
      <span className="rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-bold text-brand-600">
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // SCHOOL CODE
  // =========================================================

  columnHelper.accessor("schoolCode", {
    id: "schoolCode",

    header: "Code",

    cell: (info) => (
      <span className="font-medium text-gray-700">
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // SCHOOL NAME
  // =========================================================

  columnHelper.accessor("name", {
    id: "name",

    header: "School Name",

    cell: (info) => (
      <div>
        <div className="font-semibold text-gray-800">
          {info.getValue() || "-"}
        </div>

        {info.row.original.alternateName && (
          <div className="mt-1 text-xs text-gray-400">
            {info.row.original.alternateName}
          </div>
        )}
      </div>
    ),
  }),

  // =========================================================
  // BOARD
  // =========================================================

  columnHelper.accessor("board", {
    id: "board",

    header: "Board",

    cell: (info) => (
      <span className="font-medium text-gray-600">
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // ASSIGNED EMPLOYEE
  // =========================================================

  columnHelper.display({
    id: "assignedEmployee",

    header: "Assigned Employee",

    cell: ({ row }) => {
      const item = row.original;

      /*
       * Support multiple possible API response structures:
       *
       * assignedEmployee: {
       *   user: {
       *     name: "John Doe"
       *   }
       * }
       *
       * OR
       *
       * assignedEmployee: {
       *   name: "John Doe"
       * }
       *
       * OR
       *
       * employee: {
       *   user: {
       *     name: "John Doe"
       *   }
       * }
       *
       * OR direct assignedEmployeeName
       */

      const assignedEmployee =
        item?.assignedEmployee ||
        item?.employee ||
        item?.assigned_employee ||
        null;

      const employeeName =
        assignedEmployee?.user?.name ||
        assignedEmployee?.name ||
        assignedEmployee?.employeeName ||
        assignedEmployee?.employee_name ||
        item?.assignedEmployeeName ||
        item?.assigned_employee_name ||
        item?.employeeName ||
        item?.employee_name ||
        "-";

      const employeeCode =
        assignedEmployee?.employeeCode ||
        assignedEmployee?.employee_code ||
        item?.assignedEmployeeCode ||
        item?.assigned_employee_code ||
        "";

      return (
        <div className="min-w-[160px]">
          <div className="font-medium text-gray-800">
            {employeeName}
          </div>

          {employeeCode && (
            <div className="mt-1 text-xs text-gray-400">
              {employeeCode}
            </div>
          )}
        </div>
      );
    },
  }),

  // =========================================================
  // TARGET CATEGORY
  // =========================================================

  columnHelper.accessor("targetCategory", {
    id: "targetCategory",

    header: "Target",

    cell: (info) => (
      <span className="font-semibold text-brand-600">
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // STATUS
  // =========================================================

  columnHelper.accessor("status", {
    id: "status",

    header: "Status",

    cell: (info) => {
      const status = info.getValue();

      return (
        <span
          className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold capitalize ${getStatusClass(
            status
          )}`}
        >
          {status || "-"}
        </span>
      );
    },
  }),

  // =========================================================
  // ACTIONS
  // =========================================================

  columnHelper.display({
    id: "actions",

    header: "Actions",

    cell: ({ row }) => {
      const item = row.original;

      return (
        <CrudActions
          showView={typeof onView === "function"}
          onView={
            onView
              ? () => onView(item)
              : undefined
          }
          viewTitle="View School"

          showEdit={
            typeof onEdit === "function"
          }
          onEdit={
            onEdit
              ? () => onEdit(item)
              : undefined
          }
          editTitle="Edit School"

          showDelete={
            typeof onDelete === "function" &&
            item.status !== "inactive"
          }
          onDelete={
            onDelete &&
            item.status !== "inactive"
              ? () => onDelete(item.id)
              : undefined
          }
          deleteTitle="Deactivate School"
        />
      );
    },
  }),
];